import { formatCurrency } from '@shared';

import { Card, CardContent, CardDescription, CardTitle } from '@design-system';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

import { useCoverageAssessment } from '@/features/guided-financial-planning/hooks';

export function CoverageAssessmentResults() {
  const { flags } = useFeatureFlags();
  const {
    yearsTillLtc,
    allPhaseInflatedProfessionalShareCost,
    multipleSelfFundingProjectedCostCoverage,
    multiplePolicyProjectedCostCoverage,
    coverageGap,
    effectiveTotalCoverage,
    userSelectedExistingLtcPolicy,
  } = useCoverageAssessment();

  const hasGap = coverageGap > 0;
  const showExistingPolicyCard =
    flags.existingLtcPolicyTypeOnCard &&
    userSelectedExistingLtcPolicy &&
    multiplePolicyProjectedCostCoverage > 0;

  return (
    <div className="mx-auto max-w-3xl">
      {/* Metric Cards */}
      <div
        className={`mb-6 grid gap-4 md:grid-cols-2 ${showExistingPolicyCard ? 'lg:grid-cols-3' : ''}`}
      >
        <Card className="gap-2 p-6">
          <CardTitle className="text-sm font-normal text-gray-700">Self-Funding Capacity</CardTitle>
          <CardDescription className="text-primary font-sans text-3xl font-bold">
            {formatCurrency(multipleSelfFundingProjectedCostCoverage)}
          </CardDescription>
          <CardDescription className="text-sm text-gray-700">
            {yearsTillLtc}-year projection based on your assets
          </CardDescription>
        </Card>
        {showExistingPolicyCard && (
          <Card className="gap-2 p-6">
            <CardTitle className="text-sm font-normal text-gray-700">
              Existing Policy Coverage
            </CardTitle>
            <CardDescription className="text-primary font-sans text-3xl font-bold">
              {formatCurrency(multiplePolicyProjectedCostCoverage)}
            </CardDescription>
            <CardDescription className="text-sm text-gray-700">
              Projected coverage from existing LTC policy
            </CardDescription>
          </Card>
        )}
        <Card className="gap-2 p-6">
          <CardTitle className="text-sm font-normal text-gray-700">Estimated LTC Cost</CardTitle>
          <CardDescription className="font-sans text-3xl font-bold text-gray-900">
            {formatCurrency(allPhaseInflatedProfessionalShareCost)}
          </CardDescription>
          <CardDescription className="text-sm text-gray-700">
            Average long-term care expenses
          </CardDescription>
        </Card>
      </div>
      {effectiveTotalCoverage > 0 && (
        <p className="mb-4 text-center text-sm text-gray-600">
          Total coverage: {formatCurrency(effectiveTotalCoverage)}{' '}
          {flags.existingLtcPolicyTypeOnCard && userSelectedExistingLtcPolicy
            ? '(self-funding + existing policy)'
            : '(self-funding)'}
        </p>
      )}

      {/* Result Message Card */}
      <Card className="gap-2 border-gray-200 bg-white p-6 py-8 text-center">
        <CardContent className="p-0">
          {hasGap ? (
            <>
              <p className="mb-2 text-gray-700">You've got a gap of</p>
              <p className="mb-4 font-sans text-4xl font-bold text-[#F58A07]">
                {formatCurrency(coverageGap)}
              </p>
              <p className="text-gray-700">
                Do you want us to find insurance to help you close that?
              </p>
            </>
          ) : (
            <>
              <p className="mb-2 text-lg font-medium text-gray-900">
                Great news! You're well-positioned
              </p>
              <p className="text-gray-900">
                We're going to protect your assets and save you money by finding financial vehicles
                that could be more efficient in a long-term care event.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function useCoverageAssessmentVariant() {
  const { coverageGap } = useCoverageAssessment();
  const hasGap = coverageGap > 0;
  return { hasGap };
}
